import { DEMO_ORIGINS } from '@/api/demoAuth'
import type { LeadIntakeInput } from '@/features/pipeline/schemas/leadIntakeSchema'
import type {
  Lead,
  LeadGateway,
  LeadIntakeResult,
  LeadSource,
  Stage,
} from '@/features/pipeline/types'
import { splitFullName } from '@/lib/utils/names'
import type { PipelineState } from '@/types/models'

/**
 * The eleven contract stages, in board order — the same catalogue the session
 * sends as `stages`, so demo mode renders the same columns as the real board.
 * The Odoo-era stages this replaced (Beratung, Anzahlung, 3 or Less, …) have no
 * counterpart in the backend's K-12 model.
 */
export const DEMO_STAGES: Stage[] = [
  { id: 'new', name: 'Neu', section: 'outreach' },
  { id: 'recontact', name: 'Erneute Kontaktaufnahme', section: 'outreach' },
  { id: 'rest_phase', name: 'Ausgesetzt', section: 'outreach' },
  { id: 'recontact_r2', name: 'Erneute Kontaktaufnahme Runde 2', section: 'outreach' },
  { id: 'trial_booked', name: 'Probebehandlung gebucht', section: 'trial' },
  { id: 'trial_cancelled', name: 'Probebehandlung storniert', section: 'trial' },
  { id: 'trial_no_show', name: 'Probebehandlung nicht erschienen', section: 'trial' },
  { id: 'trial_attended', name: 'Probebehandlung stattgefunden', section: 'trial' },
  { id: 'follow_up', name: 'Nachfassen', section: 'trial' },
  { id: 'won_active', name: 'Gewonnen / Aktiv', section: 'active_customer' },
  { id: 'exited', name: 'Ausgeschieden', section: 'closed' },
]

const NAMES = [
  'Jessica Walter',
  'Lana Hvy Dinh',
  'Anna Ziegler',
  'Achogu Blessing Rita',
  'Adama Sumaila',
  'Adel Ibrahim',
  'Ahlem Gerges',
  'Aida Jakupovic',
  'Akua Gyamfua',
  'Alexandra Leotta',
  'Alexandru Belecciu',
  'Ashmitha Farah',
  'Amro Ahmed',
  'Ana-Marija Tomsic',
  'Antonella Stanzu',
  'Atidja Elezi',
  'Barbara Schwab',
  'Birgit Riemer',
]

/**
 * Contract stage codes only — every entry must match a `DEMO_STAGES` id, or the
 * card lands in a column that does not exist and silently disappears from the
 * board. Spread across all four sections so demo mode exercises each colour.
 */
const STAGE_CYCLE = [
  'new',
  'recontact',
  'rest_phase',
  'recontact_r2',
  'trial_booked',
  'trial_attended',
  'follow_up',
  'won_active',
  'exited',
]

const NEXT_ACTIVITIES = ['Heute anrufen', 'Termin bestätigen', 'Rückruf planen', 'Angebot senden']

function isoDate(offsetDays: number): string {
  const date = new Date()
  date.setHours(12, 0, 0, 0)
  date.setDate(date.getDate() + offsetDays)
  return date.toISOString().slice(0, 10)
}

function websiteGateway(leadId: number): LeadGateway {
  return {
    message: 'Hallo :) Bekomme ich das tatsächlich für ca. 79 €? Vielen Dank',
    form: 'Kontaktangaben_CRM',
    studio: 'Stuttgart/Fellbach',
    externalId: `wix-${leadId}729566`,
  }
}

function seedLeads(): Lead[] {
  return NAMES.map((name, index) => {
    const handle = name
      .toLowerCase()
      .replace(/[’'\s.-]/g, '')
      .slice(0, 12)
    const packageTag =
      index % 5 === 0 ? 'Premium-Paket' : index % 3 === 0 ? 'Basic-Paket' : undefined
    const source: LeadSource =
      index % 4 === 0 ? 'WIX Website' : index % 7 === 0 ? 'Manuell' : 'Empfehlung'
    // −1 puts a fifth of the board overdue, 0 makes today's calls visible.
    const dueOffset = (index % 5) - 1

    return {
      id: String(1000 + index),
      customerId: String(500 + index),
      name,
      contactName: name,
      email: `${handle}${index + 1}@gmx.net`,
      phone: `+49 17${(560000000 + index * 1937).toString().slice(-8)}`,
      stageId: STAGE_CYCLE[index % STAGE_CYCLE.length],
      expectedRevenue: index % 6 === 0 ? 1763.87 : index % 5 === 0 ? 1343.7 : 0,
      probability: [25, 40, 55, 70, 85, 98][index % 6],
      tags: [source, packageTag].filter(Boolean) as string[],
      source,
      owner: 'Administrator',
      nextActivity: NEXT_ACTIVITIES[index % NEXT_ACTIVITIES.length],
      nextActivityDate: isoDate(dueOffset),
      overdue: dueOffset < 0,
      noAnswerToday: index % 3,
      noAnswerCount: index % 4,
      gateway: source === 'WIX Website' ? websiteGateway(1000 + index) : undefined,
    }
  })
}

const INBOUND_NAMES = [
  'Claudia März',
  'Daniela Gareis',
  'Elisa Mueller',
  'Eleni Papadopoulou',
  'Barbara Schwab',
]

// Only "Jessica Walter" should stay as the "Neu" example — the cycle would
// otherwise also land Akua Gyamfua and Barbara Schwab in that stage.
let leads = seedLeads().filter(
  (lead) => lead.name !== 'Akua Gyamfua' && lead.name !== 'Barbara Schwab',
)

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function pipelineStateOf(lead: Lead): PipelineState {
  const stage = DEMO_STAGES.find((candidate) => candidate.id === lead.stageId)

  return {
    stage: lead.stageId,
    stageLabel: stage?.name ?? lead.stageId,
    stageSection: stage?.section ?? 'outreach',
    attemptCount: lead.noAnswerCount ?? 0,
    attemptsToday: lead.noAnswerToday ?? 0,
    blockedForToday: (lead.noAnswerToday ?? 0) >= 3,
    dueToday: true,
    nextActionAt: null,
    restPhaseUntil: null,
    salesProcessClosed: false,
    manualOverride: false,
  }
}

export const demoPipeline = {
  fetchColumn: async (stage: string, page: number, size: number) => {
    await wait(280)
    const matches = leads.filter((lead) => lead.stageId === stage)
    return {
      leads: matches.slice(page * size, page * size + size).map((lead) => ({ ...lead })),
      page,
      size,
      total: matches.length,
    }
  },

  createLead: async (input: {
    name: string
    contactName?: string
    stageId: string
    email?: string
    phone?: string
    expectedRevenue: number
    probability: number
    /** Set when the lead comes in through the studio intake form. */
    studio?: string
    notes?: string
  }): Promise<Lead> => {
    await wait(320)

    // The intake form's studio field marks a lead as inbound, same as a WIX submission.
    const fromIntake = Boolean(input.studio)

    const lead: Lead = {
      id: `lead-${Date.now()}`,
      customerId: `cust-${Date.now()}`,
      name: input.name,
      contactName: input.contactName?.trim() || input.name,
      email: input.email ?? '',
      phone: input.phone ?? '',
      stageId: input.stageId,
      expectedRevenue: input.expectedRevenue,
      probability: input.probability,
      tags: [fromIntake ? 'WIX Website' : 'Manuell'],
      source: fromIntake ? 'WIX Website' : 'Manuell',
      owner: 'Administrator',
      nextActivity: 'Erstkontakt',
      nextActivityDate: isoDate(0),
      overdue: false,
      noAnswerToday: 0,
      noAnswerCount: 0,
      gateway: fromIntake
        ? {
            message: input.notes?.trim() || 'Termin angefragt über das Intake-Formular.',
            form: 'Termin_Anfragen',
            studio: input.studio ?? '',
            externalId: `intake-${Date.now()}`,
          }
        : undefined,
    }

    leads = [lead, ...leads]
    return lead
  },

  submitLeadIntake: async (input: LeadIntakeInput): Promise<LeadIntakeResult> => {
    const email = input.email.trim().toLowerCase()
    const originLabel =
      DEMO_ORIGINS.find((option) => option.origin === input.origin)?.originLabel ?? input.origin
    const existing = leads.find((lead) => lead.email.toLowerCase() === email)

    if (!existing) {
      const lead = await demoPipeline.createLead({
        name: input.name,
        contactName: input.name,
        stageId: 'new',
        email,
        phone: input.phone,
        expectedRevenue: 0,
        probability: 25,
        studio: input.studio,
        notes: input.notes,
      })

      return {
        customerId: lead.customerId,
        created: true,
        enrichedFields: [],
        name: input.name.trim(),
        email,
        phone: lead.phone,
        origin: input.origin,
        originLabel,
        pipeline: pipelineStateOf(lead),
      }
    }

    await wait(320)

    const incoming = splitFullName(input.name)
    const stored = splitFullName(existing.contactName ?? existing.name)
    const enrichedFields: string[] = (['firstName', 'lastName'] as const).filter(
      (part) => incoming[part] !== undefined && incoming[part] !== stored[part],
    )
    if (input.phone.trim() !== existing.phone) enrichedFields.push('phone')

    const enriched: Lead = {
      ...existing,
      name: input.name.trim(),
      contactName: input.name.trim(),
      phone: input.phone.trim(),
    }
    leads = leads.map((lead) => (lead.id === existing.id ? enriched : lead))

    return {
      customerId: enriched.customerId,
      created: false,
      enrichedFields,
      name: enriched.name,
      email: enriched.email,
      phone: enriched.phone,
      origin: input.origin,
      originLabel,
      pipeline: pipelineStateOf(enriched),
    }
  },

  /**
   * The three card actions. Without the scheduler and sales modules behind
   * them, each one records its outcome on the lead — which is what the board
   * shows — so the effect of submitting is visible.
   */
  planActivity: async (input: {
    leadId: string
    dueDate: string
    summary: string
  }): Promise<void> => {
    await wait(320)
    leads = leads.map((lead) =>
      lead.id === input.leadId
        ? {
            ...lead,
            nextActivity: input.summary,
            nextActivityDate: input.dueDate,
            overdue: input.dueDate < isoDate(0),
          }
        : lead,
    )
  },

  bookAppointment: async (input: {
    leadId: string
    date: string
    start: string
  }): Promise<void> => {
    await wait(320)
    const day = new Date(`${input.date}T12:00:00`).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    })

    leads = leads.map((lead) =>
      lead.id === input.leadId
        ? {
            ...lead,
            nextActivity: `Termin am ${day} um ${input.start}`,
            nextActivityDate: input.date,
            overdue: false,
          }
        : lead,
    )
  },

  createSale: async (_input: { leadId: string }): Promise<void> => {
    await wait(320)
  },

  moveLead: async (leadId: string, stageId: string) => {
    await wait(180)
    leads = leads.map((lead) => (lead.id === leadId ? { ...lead, stageId } : lead))
  },

  simulateLead: async () => {
    await wait(220)
    const index = leads.length
    const name = INBOUND_NAMES[index % INBOUND_NAMES.length]
    const handle = name
      .toLowerCase()
      .replace(/[’'\s.-]/g, '')
      .slice(0, 12)

    leads = [
      {
        id: `sim-${Date.now()}`,
        customerId: `sim-${Date.now()}`,
        name,
        email: `${handle}@gmx.net`,
        phone: '+49 170 5551234',
        stageId: 'new',
        expectedRevenue: 0,
        probability: 25,
        tags: ['WIX Website'],
        source: 'WIX Website',
        owner: 'Administrator',
        nextActivity: 'Heute anrufen',
        nextActivityDate: isoDate(0),
        overdue: false,
        noAnswerToday: 0,
        noAnswerCount: 0,
        gateway: websiteGateway(Date.now()),
      },
      ...leads,
    ]
  },
}
